import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { CheckCircle, Mail, Search, Calendar, HardDrive, Filter, Mic, MessageSquare, Phone, Target } from 'lucide-react';
import { cn } from '@/lib/utils';
export function Pricing() {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [isBeta] = useState(true); // Feature flag for beta mode

  const getDisplayPrice = (basePrice: number) => {
    if (isBeta && basePrice > 0) return 0; // Free during beta for paid plans
    if (billingCycle === 'annual') {
      return Math.round(basePrice * 0.8); // 20% discount for annual
    }
    return basePrice;
  };
  const plans = [{
    name: 'Free',
    basePrice: 0,
    features: [{
      text: '10 emails per month',
      icon: Mail,
      description: 'Perfect for trying out RealtyZen'
    }, {
      text: 'Basic tone matching',
      icon: CheckCircle,
      description: 'AI learns your communication style'
    }, {
      text: 'Email integration',
      icon: CheckCircle,
      description: 'Works with Gmail'
    }],
    cta: 'Start Free',
    ctaVariant: 'outline' as const,
    popular: false
  }, {
    name: 'Plus',
    basePrice: 25,
    features: [{
      text: 'Unlimited email drafts',
      icon: Mail,
      description: 'No monthly limits'
    }, {
      text: 'AI tone matching',
      icon: CheckCircle,
      description: 'AI learns your communication style'
    }, {
      text: 'Web search & research',
      icon: Search,
      description: 'AI researches property details'
    }, {
      text: 'Calendar integration',
      icon: Calendar,
      description: 'Sync with your schedule'
    }, {
      text: 'Google Drive integration',
      icon: HardDrive,
      description: 'Access your documents'
    }, {
      text: 'Smart inbox filtering',
      icon: Filter,
      description: 'Auto-prioritize important messages'
    }],
    cta: isBeta ? 'Join Beta' : 'Start Plus',
    ctaVariant: 'default' as const,
    popular: true,
    betaNote: isBeta ? 'Free during beta' : null
  }, {
    name: 'Pro',
    basePrice: 50,
    features: [{
      text: 'Everything in Plus, plus:',
      icon: CheckCircle,
      description: 'All Plus features included'
    }, {
      text: 'Voice assistant',
      icon: Mic,
      description: 'Approve emails by voice'
    }, {
      text: 'SMS integration',
      icon: MessageSquare,
      description: 'Handle text messages too'
    }, {
      text: 'WhatsApp integration',
      icon: MessageSquare,
      description: 'Manage WhatsApp messages'
    }, {
      text: 'Phone integration',
      icon: Phone,
      description: 'Log calls automatically'
    }, {
      text: 'Proactive lead nurturing',
      icon: Target,
      description: 'AI drafts outreach to prospects'
    }],
    cta: 'Coming Soon',
    ctaVariant: 'outline' as const,
    popular: false,
    betaNote: null,
    comingSoon: true
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const cardVariants = {
    hidden: {
      y: 20,
      opacity: 0
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="pricing" className="relative py-16 sm:py-24 overflow-hidden" data-id="element-325">
      {/* Pure white background */}
      <div className="absolute inset-0 bg-white" data-id="element-326" />
      
      {/* Single clean gradient accent */}
      <div className="absolute inset-0" style={{
      background: 'linear-gradient(180deg, transparent 0%, rgba(94, 130, 98, 0.02) 100%)'
    }} data-id="element-327" />
      
      <div className="container relative" data-id="element-328">
        {/* Section Header */}
        <motion.div className="text-center mb-8" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-329">
          <h2 className="font-display text-4xl md:text-5xl mb-4" data-id="element-330">
            Simple, transparent pricing
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto" data-id="element-331">
            {isBeta ? 'Free during beta. No credit card required.' : 'Choose the plan that fits your needs.'}
          </p>
        </motion.div>
        
        {/* Billing Toggle - Always visible */}
        <motion.div className="flex items-center justify-center gap-6 mb-8" initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.4,
        delay: 0.2
      }} data-id="element-332">
          <span className={cn("text-sm font-medium transition-colors", billingCycle === 'monthly' ? "text-foreground" : "text-muted-foreground")} data-id="element-333">
            Monthly
          </span>
          <Switch checked={billingCycle === 'annual'} onCheckedChange={checked => setBillingCycle(checked ? 'annual' : 'monthly')} className="data-[state=checked]:bg-sage-600" data-id="element-334" />
          <span className={cn("text-sm font-medium transition-colors", billingCycle === 'annual' ? "text-foreground" : "text-muted-foreground")} data-id="element-335">
            Annual
            <Badge className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-1" data-id="element-336">
              Save 20%
            </Badge>
          </span>
        </motion.div>
        
        {/* Pricing Cards */}
        <motion.div className="grid lg:grid-cols-2 gap-8 max-w-4xl mx-auto" variants={containerVariants} initial="hidden" animate="visible" data-id="element-337">
          {plans.filter(plan => plan.name !== 'Free').map((plan, index) => <motion.div key={index} variants={cardVariants} className="h-full" data-id="element-338">
              <Card className={cn("relative p-6 h-full flex flex-col", "bg-white/75 backdrop-blur-[20px]", "border border-white/20 shadow-lg", "transition-all duration-300 ease-out group", "hover:shadow-2xl hover:bg-white/85 hover:-translate-y-1", "before:absolute before:inset-0 before:rounded-lg before:p-[1px]", "before:bg-gradient-to-r before:from-transparent before:via-sage-400/30 before:to-transparent", "before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500", "before:-z-10", plan.popular && "ring-2 ring-sage-600/20 border-sage-300/30")} data-id="element-339">
                {/* Free During Beta badge */}
                {plan.popular && isBeta && <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10" data-id="element-340">
                    <Badge className="bg-sage-600 text-white px-3 py-1 text-sm font-medium shadow-md border border-sage-700 animate-glow" data-id="element-341">
                      Free During Beta
                    </Badge>
                  </div>}
                
                
                {/* Plan name - fixed height */}
                <div className="h-16 mb-4 pt-2" data-id="element-342">
                  <h3 className="font-display text-xl md:text-2xl" data-id="element-343">{plan.name}</h3>
                </div>
                
                {/* Clean pricing display - aligned structure */}
                <div className="min-h-[80px] mb-4" data-id="element-344">
                  {/* Beta pricing (Plus) - show actual price */}
                  {isBeta && plan.basePrice > 0 ? <div className="space-y-2" data-id="element-345">
                      <div className="flex items-baseline gap-2" data-id="element-346">
                        <span className="text-4xl md:text-5xl font-bold tracking-tight" data-id="element-347">
                          ${billingCycle === 'annual' ? Math.round(plan.basePrice * 0.8) : plan.basePrice}
                        </span>
                        <span className="text-lg text-muted-foreground" data-id="element-348">
                          /month • billed {billingCycle === 'annual' ? 'annually' : 'monthly'}
                        </span>
                      </div>
                      {!plan.comingSoon && <p className="text-sm text-sage-600 font-medium" data-id="element-349">
                          Currently free during beta
                        </p>}
                      {plan.comingSoon && <div className="h-5" data-id="element-350"></div>}
                    </div> : /* Coming Soon (Pro) - show future pricing clearly */
              plan.comingSoon ? <div className="space-y-2" data-id="element-351">
                      <div className="flex items-baseline gap-2" data-id="element-352">
                        <span className="text-4xl md:text-5xl font-bold tracking-tight text-muted-foreground" data-id="element-353">
                          ${billingCycle === 'annual' ? Math.round(plan.basePrice * 0.8) : plan.basePrice}
                        </span>
                        <div className="flex items-center gap-2" data-id="element-354">
                          <span className="text-lg text-muted-foreground" data-id="element-355">
                            /month • billed {billingCycle === 'annual' ? 'annually' : 'monthly'}
                          </span>
                          <span className="px-2 py-0.5 bg-gradient-to-r from-gray-400 to-gray-500 text-white text-xs rounded-full font-medium animate-pulse shadow-sm" data-id="element-356">
                            Soon
                          </span>
                        </div>
                      </div>
                      {/* Empty div to maintain vertical spacing consistency */}
                      <div className="h-5" data-id="element-357"></div>
                    </div> : (/* Regular pricing (fallback) */

              <div className="space-y-2" data-id="element-358">
                      <div className="flex items-baseline gap-2" data-id="element-359">
                        <span className="text-4xl md:text-5xl font-bold tracking-tight" data-id="element-360">
                          ${getDisplayPrice(plan.basePrice)}
                        </span>
                        <span className="text-lg text-muted-foreground" data-id="element-361">
                          /month • billed {billingCycle === 'annual' ? 'annually' : 'monthly'}
                        </span>
                      </div>
                      <div className="h-5" data-id="element-362"></div>
                    </div>)}
                </div>
                
                {/* Features list with micro-interactions */}
                <ul className="space-y-3 mb-6 flex-grow" data-id="element-363">
                  {plan.features.map((feature, i) => {
                const Icon = feature.icon;
                return <li key={i} className="flex items-start gap-3" data-id="element-364">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-sage-100 flex items-center justify-center mt-0.5" data-id="element-365">
                          <Icon className="w-3 h-3 text-sage-600" data-id="element-366" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-367">
                          <p className="text-sm leading-relaxed text-foreground" data-id="element-368">
                            {feature.text}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed" data-id="element-369">
                            {feature.description}
                          </p>
                        </div>
                      </li>;
              })}
                </ul>
                
                {/* CTA Button with shimmer effect */}
                <Button variant={plan.ctaVariant} disabled={plan.comingSoon} className={cn("w-full py-2.5 text-sm font-medium transition-all duration-300 mt-auto", "btn-shimmer", plan.popular ? "bg-primary hover:bg-sage-700 text-white hover:shadow-lg" : "border border-sage-300 text-sage-700 hover:bg-sage-50 hover:border-sage-500 hover:text-sage-800", plan.comingSoon && "opacity-50 cursor-not-allowed")} onClick={() => !plan.comingSoon && (window.location.href = '/waitlist')} data-id="element-370">
                  {plan.cta}
                </Button>
              </Card>
            </motion.div>)}
        </motion.div>
        
      </div>
    </section>;
}